import classNames from "classnames";
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useCarDriversApi } from "../../http/carDrivers/api";
import { useCarPlateApi } from "../../http/carPlates/api";
import { AssignNewCarPlate } from "../../http/carPlates/types";
import { useAdminCarsApi } from "../../http/cars/api";
import { getCarStateColor, getCategories, getFuelTypes, getStatus, getTypesOfUse } from "../../http/cars/types";
import { useCarTaskLogsApi } from "../../http/carTaskLogs/api";
import { CarTaskDone, CarTaskNotRequired } from "../../http/carTaskLogs/types";
import { useCarTaskApi } from "../../http/carTasks/api";
import { CarTaskNoCarDependency, CarTaskState, getTaskStateColor, getTaskStateText } from "../../http/carTasks/types";
import { useDueDatesApi } from "../../http/dueDates/api";
import { DueDate, isItemExpired, isItemExpiringSoon, UpsertDueDate } from "../../http/dueDates/types";
import { useHighwayPassesApi } from "../../http/highwayPasses/api";
import { AssignNewHighwayPass } from "../../http/highwayPasses/types";
import { useOtherCarAssignmentApi } from "../../http/otherCarAssignment/api";
import { RentalCar } from "../../http/rentalCars/types";
import { useAdminWorkflowsApi } from "../../http/workflow/api";
import { getIconProps } from "../../http/workflow/types";
import { NestedPick } from "../../types";
import {
  convertKilometersToMiles,
  downloadFileFromData,
  formatDateNumeric,
  formatNumber,
  getTitle,
} from "../../types/utils";
import CarDriverForm, { CarDriverFormBody } from "../carDrivers/CarDriverForm";
import EditCarDriverDialog from "../carDrivers/EditCarDriverDialog";
import CreateCarLeasing from "../carLeasings/CreateCarLeasingDialog";
import EditCarLeasingDialog from "../carLeasings/EditCarLeasingDialog";
import CarPlateForm, { CarPlateFormBody } from "../carPlates/CarPlateForm";
import EditCarPlateDialog from "../carPlates/EditCarPlateDialog";
import CarSubsidiariesDialog from "../CarSubsidiaries/CarSubsidiariesDialog";
import CreateCarSubsidiaryButton from "../CarSubsidiaries/CreateCarSubsidiaryButton";
import CarTasksHistory from "../carTask/CarTasksHistory";
import CreateCarTaskDialog from "../carTask/CreateCarTaskDialog";
import DeleteCarTaskDialog from "../carTask/DeleteCarTaskDialog";
import EditCarTaskDialog from "../carTask/EditCarTaskDialog";
import CreateDueDateDialog from "../dueDates/CreateDueDateDialog";
import DeleteDueDateDialog from "../dueDates/DeleteDueDateDialog";
import DueDateFormNuovo, { DueDateFormBody } from "../dueDates/DueDateForm";
import EditDueDate from "../dueDates/EditDueDateDialog";
import DueDateTypes from "../dueDates/types/DueDateTypes";
import CreateHighwayPassDialog from "../highwayPasses/CreateHighwayPassDialog";
import EditHighwayPassDialog from "../highwayPasses/EditHighwayPassButton";
import HighwayPassForm, { HighwayPassFormBody } from "../highwayPasses/HighwayPassForm";
import SvgAddBox from "../icons/AddBox";
import SvgAddHomeWork from "../icons/AddHomeWork";
import SvgAddTask from "../icons/AddTask";
import SvgAssignmentTurnedIn from "../icons/AssignmentTurnedIn";
import SvgCalendar from "../icons/CalendarMonth";
import SvgCancel from "../icons/Cancel";
import SvgCarNotInUse from "../icons/CarNotInUse";
import SvgCarRental from "../icons/CarRental";
import SvgCheckCircle from "../icons/CheckCircle";
import SvgCheck from "../icons/CheckIcon";
import SvgDoNotDisturbOn from "../icons/DoNotDisturbOn";
import SvgDownload from "../icons/Download";
import SvgEdit from "../icons/Edit";
import SvgNotifications from "../icons/Notifications";
import SvgParkingSign from "../icons/ParkingSing";
import SvgSwapDrivingApps from "../icons/SwapDrivingApps";
import SvgWarning from "../icons/Warning";
import CreateMileageDialog from "../mileages/CreateMileageDialog";
import EditMileageDialog from "../mileages/EditMileageDialog";
import SendMileageReminderDialog from "../mileages/SendMileageReminderDialog";
import EditOtherCarAssignmentDialog from "../otherCarAssignment/EditOtherCarAssignmentDialog";
import OtherCarAssignmentForm, { OtherCarAssignmentFormBody } from "../otherCarAssignment/OtherCarAssignmentForm";
import CreateRentalCarDialog from "../rentalCars/CreateRentalCarDialog";
import EditRentalCarDialog from "../rentalCars/EditRentalCarDialog";
import Box, { ListAction } from "../ui/Box";
import { Check } from "../ui/Check";
import CustomLink from "../ui/CustomLink";
import Icon from "../ui/Icon";
import Inline from "../ui/Inline";
import { Action, getDeleteAction, getEditAction, getHistoryAction } from "../ui/ListItemMenu";
import LoadingSpinner from "../ui/LoadingSpinner";
import ModalDialog from "../ui/ModalDialog";
import { State } from "../ui/State";
import Table from "../ui/Table";
import CheckWorkflowDialog from "../workflows/CheckWorkflowDialog";
import AgreementForm, { AgreementFormBody } from "./AgreementForm";
import DelegationForm, { DelegationFormBody } from "./DelegationForm";
import EditCarFormDialog from "./EditCarForm";
import styles from "./Overview.module.css";
import OverviewGrid from "./OverviewGrid";

const CAR_TASK_FIELDS = [
  "id",
  "car.id",
  "state",
  "taskName",
  "driver.id",
  "driver.fullName",
  "defaultTask",
  "defaultTask.id",
  "defaultTask.name",
  "defaultTask.defaultDueDateType.id",
  "defaultTask.defaultDueDateType.name",
  "workflowState",
  "workflowId",
  "workflowCreatedAt",
] as const;
type CarTaskField = (typeof CAR_TASK_FIELDS)[number];
type CarTaskSearch = NestedPick<CarTaskNoCarDependency, CarTaskField>;

const empty = "---";

export type OverviewPropsHandle = {
  reloadData: () => void;
};

type OverviewProps = {
  setShowCarPlatesTab: (value: boolean) => void;
  setError: (value: boolean) => void;
};

const Overview = forwardRef<OverviewPropsHandle, OverviewProps>(({ setShowCarPlatesTab, setError }, ref) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);
  const navigate = useNavigate();
  const { id: carId } = useParams<{ id: string }>();

  const carsApi = useAdminCarsApi();
  const carTasksApi = useCarTaskApi();
  const carTaskLogsApi = useCarTaskLogsApi();
  const carDriversApi = useCarDriversApi();
  const highwayPassesApi = useHighwayPassesApi();
  const carPlatesApi = useCarPlateApi();
  const otherCarAssignmentApi = useOtherCarAssignmentApi();
  const dueDatesApi = useDueDatesApi();
  const workflowsApi = useAdminWorkflowsApi();

  const [showEditCar, setShowEditCar] = useState(false);
  const [showEditCarDriver, setShowEditCarDriver] = useState(false);
  const [showAssignCarDriver, setShowAssignCarDriver] = useState(false);
  const [showNewCarDriver, setShowNewCarDriver] = useState(false);
  const [showHp, setShowHp] = useState(false);
  const [showNewHp, setShowNewHp] = useState(false);
  const [showAssignNewHp, setShowAssignNewHp] = useState(false);
  const [showLeasing, setShowLeasing] = useState(false);
  const [showNewLeasing, setShowNewLeasing] = useState(false);
  const [showMileages, setShowMileages] = useState(false);
  const [showNewMileage, setShowNewMileage] = useState(false);
  const [showSendMileageReminder, setShowSendMileageReminder] = useState(false);
  const [showNewDueDate, setShowNewDueDate] = useState(false);
  const [itemToCreateDueDate, setItemToCreateDueDate] = useState<CarTaskSearch>();
  const [dueDateToEdit, setDueDateShow] = useState<DueDate>();
  const [dueDateToDelete, setDueDateToDelete] = useState<DueDate>();
  const [itemToShowHistory, setItemToShowHistory] = useState<CarTaskSearch>();
  const [showCarPlate, setShowCarPlate] = useState(false);
  const [showAssignCarPlate, setShowAssignCarPlate] = useState(false);
  const [showNewCarTask, setShowNewCarTask] = useState<{ isOpen: boolean; isWithDefault: boolean }>({
    isOpen: false,
    isWithDefault: false,
  });
  const [carTaskToEdit, setCarTaskToEdit] = useState<CarTaskSearch>();
  const [carTaskToDelete, setCarTaskToDelete] = useState<CarTaskSearch>();
  const [showAgreementDialog, setShowAgreementDialog] = useState(false);
  const [showCarDriverDelegationDialog, setShowCarDriverDelegationDialog] = useState(false);
  const [showCarDelegationDialog, setShowCarDelegationDialog] = useState(false);
  const [showCarDelegationExternalDialog, setShowCarDelegationExternalDialog] = useState(false);
  const [showAddToOtherCarAssignment, setShowAddToOtherCarAssignment] = useState<{
    value: boolean;
    type?: string;
  }>({ value: false });
  const [showEditOtherCarAssignment, setShowEditOtherCarAssignment] = useState(false);
  const [showDueDateTypes, setShowDueDateTypes] = useState(false);
  const [showCarSubsidiariesDialog, setShowCarSubsidiariesDialog] = useState(false);
  const [showAssignSubsidiary, setShowAssignSubsidiary] = useState(false);
  const [isSubmittingNotify, setIsSubmittingNotify] = useState(false);
  const [showNotifyDialog, setShowNotifyDialog] = useState<CarTaskSearch>();
  const [showCheckWorkflow, setShowCheckWorkflow] = useState<CarTaskSearch>();
  const [showNewRental, setShowNewRental] = useState(false);
  const [showEditRental, setShowEditRental] = useState<RentalCar | undefined>();

  const { data, error: overViewError, trigger } = carsApi.getCarOverview(carId!, aborter.current.signal);
  const [_, isInitialized] = useDataInitializer(data?.car, overViewError);

  const hasSubsidiaryPermissions =
    !!data?.car.carSubsidiary?.subsidiary.id &&
    userSettings.me.userSubsidiariesIDs.includes(data.car.carSubsidiary.subsidiary.id);

  useEffect(() => {
    carId && trigger();
    setError(!!overViewError);
  }, [carId]);

  useImperativeHandle(ref, () => ({
    reloadData() {
      trigger();
      setError(!!overViewError);
    },
  }));

  useEffect(() => {
    overViewError && dispatch(showErrorNotification(i18n, overViewError));
    setError(!!overViewError);
  }, [overViewError]);

  useEffect(() => {
    setShowCarPlatesTab(!!data?.car.carSubsidiary?.subsidiary.allowMultiPlates);
  }, [data?.car.carSubsidiary?.subsidiary.allowMultiPlates]);

  const adminCarTasksResult = carTasksApi.useAdminCarTasks(
    {
      filters: [{ fieldname: "carTask.task.state", values: ["to-do"] }],
      fields: CAR_TASK_FIELDS,
    },
    aborter.current.signal,
    carId,
    !!userSettings.permissions.carTasks.read,
  );

  const [tasks, areTasksInitialized] = useDataInitializer(adminCarTasksResult?.data, adminCarTasksResult?.error);

  const param = new URLSearchParams(window.location.search);
  const workflowId = param.get("wf_id");
  useEffect(() => {
    if (workflowId && areTasksInitialized) {
      const workflow = tasks?.items.find((t) => t.workflowId === workflowId);
      workflow && setShowCheckWorkflow(workflow);
    }
  }, [workflowId, areTasksInitialized]);

  const closeWorkflowDialog = () => {
    setShowCheckWorkflow(undefined);
    if (workflowId) {
      const url = new URL(window.location.href);
      url.searchParams.delete("wf_id");
      window.history.replaceState({}, document.title, url.toString());
    }
  };

  const assignNewDriver = async (formData: CarDriverFormBody): Promise<void> => {
    try {
      if (!!formData.prevEndDate) {
        await carDriversApi.assignNewDriver({
          carId: carId!,
          driverId: formData.driver.id,
          startDate: formData.startDate,
          endDate: formData.endDate,
          typeOfUse: formData.typeOfUse.value,
          sendMileageEmail: formData.sendMileageEmail,
          prevEndDate: formData.prevEndDate,
          currentVersionNumber:
            data?.currentAssignment.carDriver?.versionNumber ??
            data?.currentAssignment.otherCarAssignment?.versionNumber,
        });
      } else {
        await carDriversApi.insertAdminCarDriver({
          carId: carId!,
          driverId: formData.driver.id,
          startDate: formData.startDate,
          endDate: formData.endDate,
          typeOfUse: formData.typeOfUse.value,
          sendMileageEmail: formData.sendMileageEmail,
        });
      }
      trigger();
      dispatch(showSuccessNotification(i18n.t));
      setShowNewCarDriver(false);
      setShowAssignCarDriver(false);
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const assignNewHighwayPass = async (formData: HighwayPassFormBody): Promise<void> => {
    const body: AssignNewHighwayPass = {
      carId: carId!,
      nr: formData.nr,
      startDate: formData.startDate,
      endDate: formData.endDate,
      prevEndDate: formData.prevEndDate ?? "",
    };

    try {
      await highwayPassesApi.assignNewHighwayPass(body);
      trigger();
      dispatch(showSuccessNotification(i18n.t));
      setShowAssignNewHp(false);
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const assignNewPlate = async (formData: CarPlateFormBody): Promise<void> => {
    const body: AssignNewCarPlate = {
      carId: carId!,
      nr: formData.nr,
      startDate: formData.startDate,
      endDate: formData.endDate,
      prevEndDate: formData.prevEndDate ?? "",
      currentVersionNumber: data?.carPlate?.versionNumber!,
    };

    try {
      await carPlatesApi.assignNewPlate(body);
      trigger();
      dispatch(showSuccessNotification(i18n.t));
      trigger();
      setShowAssignCarPlate(false);
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const toggle = async (carTask: CarTaskSearch, state: CarTaskState) => {
    try {
      await carTaskLogsApi.insertAdminCarTaskLog(
        carTask.id,
        {
          state: state,
        },
        carTask.car.id,
      );
      dispatch(showSuccessNotification(i18n.t));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
    trigger();
  };

  const addToOtherCarAssignment = async (formData: OtherCarAssignmentFormBody) => {
    try {
      if (!!formData.prevEndDate) {
        await otherCarAssignmentApi.addToOtherCarAssignment({
          carId: carId!,
          startDate: formData.startDate,
          endDate: formData.endDate,
          prevEndDate: formData.prevEndDate,
          costCenter: formData.costCenter,
          type: formData.type.value,
          currentVersionNumber:
            data?.currentAssignment.carDriver?.versionNumber ??
            data?.currentAssignment.otherCarAssignment?.versionNumber,
        });
      } else {
        await otherCarAssignmentApi.insertOtherCarAssignment({
          carId: carId!,
          startDate: formData.startDate,
          endDate: formData.endDate,
          costCenter: formData.costCenter,
          type: formData.type.value,
        });
      }
      trigger();
      dispatch(showSuccessNotification(i18n.t));
      setShowAddToOtherCarAssignment({ value: false });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const onSubmitNewDueDate = async (formData: DueDateFormBody): Promise<void> => {
    const body: UpsertDueDate = {
      carId: carId ?? formData.car.id,
      typeId: formData.type.id,
      date: formData.date,
      isSettled: formData.isSettled,
      visibleToUserId: formData.visibleToUser ? formData.driver?.id : undefined,
    };

    try {
      await dueDatesApi.insertAdminDueDate(body);
      trigger();
      dispatch(showSuccessNotification(i18n.t));
      setItemToCreateDueDate(undefined);
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    }
  };

  const onSubmitAgreement = async (formData: AgreementFormBody) => {
    if (data?.currentAssignment?.carDriver && data?.currentAssignment?.carDriver?.id) {
      const r = await carDriversApi.exportAgreement(
        data.currentAssignment.carDriver?.id,
        formData.language,
        aborter.current.signal,
      );
      r && downloadFileFromData(r?.data, i18n.t.common.agreement, "application/pdf");
    }
  };

  const onSubmitCarDriverDelegation = async (formData: DelegationFormBody) => {
    if (data?.currentAssignment?.carDriver && data?.currentAssignment?.carDriver.id && carId) {
      try {
        const docId = await carDriversApi.createDelegation(data.currentAssignment.carDriver.id, carId, {
          language: formData.language.value,
        });
        dispatch(showSuccessNotification(i18n.t));
        setShowCarDriverDelegationDialog(false);
        navigate(
          `/admin/cars/${carId}?tab=${data.car.carSubsidiary?.subsidiary.allowMultiPlates ? 9 : 8}&doc_id=${docId}`,
        );
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      }
    }
  };

  const onSubmitCarDelegation = async (formData: DelegationFormBody, external?: boolean) => {
    if (carId && data) {
      try {
        const docId = await carsApi.createDelegation(carId, {
          driversName: formData.driversName
            ? formData.driversName
            : formData.driver?.fullName
              ? [formData.driver?.fullName]
              : [],
          typeOfUse: external ? "only-private" : formData.typeOfUse.value,
          language: formData.language.value,
        });
        dispatch(showSuccessNotification(i18n.t));
        setShowCarDelegationDialog(false);
        setShowCarDelegationExternalDialog(false);
        navigate(
          `/admin/cars/${carId}?tab=${data.car.carSubsidiary?.subsidiary.allowMultiPlates ? 9 : 8}&doc_id=${docId}`,
        );
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      }
    }
  };

  const notifyDriver = async (item: CarTaskSearch) => {
    setIsSubmittingNotify(true);
    try {
      await workflowsApi.createWorkflow(item.id, carId!);
      trigger();
      dispatch(showSuccessNotification(i18n.t));
      setShowNotifyDialog(undefined);
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmittingNotify(false);
    }
  };

  const getDueDateRowActions = (item: DueDate): Action[] => {
    const actions: Action[] = [];

    if (hasSubsidiaryPermissions && userSettings.permissions.dueDates.update) {
      actions.push(getEditAction(i18n, () => setDueDateShow(item)));
    }

    if (hasSubsidiaryPermissions && userSettings.permissions.dueDates.delete) {
      actions.push(getDeleteAction(i18n, () => setDueDateToDelete(item), !!actions.length));
    }

    return actions;
  };

  const getTasksRowActions = (item: CarTaskSearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.carTasks.update) {
      actions.push({
        title: i18n.t.common.setAsDone,
        onClick: () => toggle(item, CarTaskDone),
        icon: SvgCheckCircle,
        color: "green",
      });
      actions.push({
        title: i18n.t.common.setAsNotRequired,
        onClick: () => toggle(item, CarTaskNotRequired),
        icon: SvgDoNotDisturbOn,
        color: "blue",
      });
    }

    actions.push(getHistoryAction(i18n, () => setItemToShowHistory(item), !!actions.length));

    if (userSettings.permissions.dueDates.insert) {
      actions.push({
        title: i18n.t.common.addDueDate,
        onClick: () => setItemToCreateDueDate(item),
        icon: SvgCalendar,
      });
    }

    if (userSettings.permissions.carTasks.update) {
      actions.push({
        title: i18n.t.common.rename,
        onClick: () => setCarTaskToEdit(item),
        icon: SvgEdit,
      });
    }

    actions.push({
      title: i18n.t.common.notifyDriver,
      onClick: () => {
        if (!item.workflowState) {
          setShowNotifyDialog(item);
        }
      },
      visible: item.defaultTask?.id == "notify-stickers" && !item.workflowState,
      icon: SvgWarning,
      isDisabled: !item.driver,
    });

    actions.push({
      title: i18n.t.common.checkWorkflow,
      icon: SvgAssignmentTurnedIn,
      onClick: () => {
        setShowCheckWorkflow(item);
      },
      visible: item.defaultTask?.id == "notify-stickers" && !!item.workflowState,
      isDisabled: !item.driver,
    });

    if (userSettings.permissions.carTasks.delete) {
      actions.push(getDeleteAction(i18n, () => setCarTaskToDelete(item), !!actions.length));
    }

    return actions;
  };

  const getTitleDriver = (startDate: string, endDate: string): string =>
    getTitle(startDate, endDate, i18n.t.common.currentDriver, i18n.t.common.lastDriver, true);

  const getTitleHighwayPass = (startDate: string, endDate: string): string =>
    getTitle(startDate, endDate, i18n.t.common.currentHighwayPass, i18n.t.common.lastHighwayPass);

  const getTitleCarPlate = (startDate: string, endDate: string, allowMultiPlates: boolean): string =>
    !allowMultiPlates
      ? i18n.t.plateNumbers.singular
      : getTitle(startDate, endDate, i18n.t.plateNumbers.singular, i18n.t.common.lastPlate);

  const fuelTypes = getFuelTypes(i18n.t);
  const categories = getCategories(i18n.t);
  const states = getStatus(i18n.t);
  const typesOfUse = getTypesOfUse(i18n.t);

  const state = states.find((x) => x.value == data?.car.status)?.label;
  const carState = state ? (
    <div className={styles["car-state"]}>
      <State text={state} color={getCarStateColor(data?.car.status)} />
    </div>
  ) : (
    empty
  );

  const getIcon = (item: CarTaskSearch) => {
    if (!item.workflowState || !item.workflowCreatedAt) return undefined;

    const props = getIconProps(i18n.t, item.workflowState, item.workflowCreatedAt);

    return (
      <Inline title={props.title} onClick={() => setShowCheckWorkflow(item)}>
        <Icon glyph={props.glyph} className={classNames(styles.icon)} color={props.color} />
      </Inline>
    );
  };

  const generalDataListActions = (): ListAction[] => {
    if (!hasSubsidiaryPermissions || !!overViewError) {
      return [];
    }
    const list: ListAction[] = [];

    if (userSettings.permissions.carDrivers.read) {
      list.push({
        label: i18n.t.common.createDelegationWithDriverSelection,
        onClick: () => setShowCarDelegationDialog(true),
        icon: SvgDownload,
      });
      list.push({
        label: i18n.t.common.createDelegationForExternal,
        onClick: () => setShowCarDelegationExternalDialog(true),
        icon: SvgDownload,
      });
    }

    if (userSettings.permissions.subsidiaries.update) {
      list.push({
        label: i18n.t.common.assignSubsidiary,
        onClick: () => setShowAssignSubsidiary(true),
        icon: SvgAddHomeWork,
      });
    }

    if (userSettings.permissions.carPlates.update) {
      list.push({
        label: i18n.t.common.assignNewPlate,
        onClick: () => setShowAssignCarPlate(true),
        icon: SvgAddBox,
      });
    }

    if (userSettings.permissions.rentalCars.insert) {
      list.push({
        label: i18n.t.rentalCars.new,
        onClick: () => setShowNewRental(true),
        icon: SvgCarRental,
      });
    }

    return list;
  };

  const assignmentListActions = (): ListAction[] => {
    if (!!overViewError) {
      return [];
    }

    const lists: ListAction[] = [];

    if (!!data?.currentAssignment.carDriver) {
      lists.push({
        label: i18n.t.common.generateAgreement,
        onClick: () => setShowAgreementDialog(true),
        icon: SvgDownload,
        disabled: !hasSubsidiaryPermissions,
      });
      lists.push({
        label: i18n.t.common.createDelegation,
        onClick: () => setShowCarDriverDelegationDialog(true),
        icon: SvgDownload,
        disabled: !hasSubsidiaryPermissions,
      });
    }

    lists.push({
      label: i18n.t.common.assignNewDriver,
      onClick: () => setShowAssignCarDriver(true),
      icon: SvgSwapDrivingApps,
    });
    lists.push({
      label: i18n.t.common.addToCarPool,
      onClick: () => setShowAddToOtherCarAssignment({ value: true, type: "car-pool" }),
      icon: SvgParkingSign,
    });
    lists.push({
      label: i18n.t.common.setNotInUse,
      onClick: () => setShowAddToOtherCarAssignment({ value: true, type: "not-in-use" }),
      icon: SvgCarNotInUse,
    });

    return lists;
  };

  const mileageListActions = (): ListAction[] => {
    if (
      !data?.currentAssignment.carDriver ||
      !hasSubsidiaryPermissions ||
      !userSettings.permissions.mileages.insert ||
      !!overViewError
    ) {
      return [];
    }

    return [
      {
        label: i18n.t.mileages.sendReminder,
        onClick: () => setShowSendMileageReminder(true),
        icon: SvgNotifications,
      },
    ];
  };

  const carTaskListActions = (): ListAction[] => {
    if (!hasSubsidiaryPermissions) {
      return [];
    }

    const list: ListAction[] = [];

    list.push({
      label: i18n.t.common.addCustomTask,
      onClick: () => setShowNewCarTask({ isOpen: true, isWithDefault: false }),
      icon: SvgAddTask,
    });
    list.push({
      label: i18n.t.common.addDefaultTask,
      onClick: () => setShowNewCarTask({ isOpen: true, isWithDefault: true }),
      icon: SvgAssignmentTurnedIn,
    });

    return list;
  };

  return (
    <>
      <OverviewGrid>
        <div>
          <Box.Group
            boxs={[
              <Box
                title={i18n.t.common.generalData}
                onEdit={
                  data && hasSubsidiaryPermissions && !!userSettings.permissions.cars.update
                    ? () => setShowEditCar(true)
                    : undefined
                }
                listActions={generalDataListActions()}
                fields={[
                  [
                    {
                      title: i18n.t.common.vehicleIdentificationNumber,
                      data: data?.car?.vehicleIdentificationNumber ? data?.car?.vehicleIdentificationNumber : empty,
                    },
                    {
                      title: i18n.t.common.registrationDate,
                      data: data?.car?.registrationDate
                        ? formatDateNumeric(data?.car?.registrationDate, i18n.language)
                        : empty,
                    },
                  ],
                  [
                    {
                      title: i18n.t.common.purchaseDate,
                      data: data?.car?.purchaseDate ? formatDateNumeric(data?.car?.purchaseDate, i18n.language) : empty,
                    },
                    {
                      title: i18n.t.common.soldDate,
                      data: data?.car?.soldDate ? formatDateNumeric(data?.car?.soldDate, i18n.language) : empty,
                    },
                  ],
                  [
                    {
                      title: i18n.t.subsidiaries.singular,
                      showHelp: {
                        type: "info",
                        onOpen: () => setShowCarSubsidiariesDialog(true),
                      },
                      data: data?.car?.carSubsidiary?.subsidiary.name ?? empty,
                    },
                    {
                      title: i18n.t.manufacturers.singular,
                      data: data?.car?.manufacturer.name ? data?.car?.manufacturer.name : empty,
                    },
                  ],
                  [
                    {
                      title: i18n.t.common.model,
                      data: data?.car?.model ? data?.car?.model : empty,
                    },
                    {
                      title: i18n.t.common.engineCapacity,
                      data: data?.car?.displacement?.toString() ?? empty,
                    },
                  ],
                  [
                    {
                      title: i18n.t.common.hp,
                      data: data?.car?.hp?.toString() ?? empty,
                    },
                    {
                      title: i18n.t.common.fuelType,
                      data: fuelTypes.find((x) => x.value == data?.car.fuelType)?.label ?? empty,
                    },
                  ],
                  [
                    {
                      title: i18n.t.common.state,
                      data: carState,
                    },
                    {
                      title: i18n.t.common.privateCar,
                      data: !data ? (
                        <></>
                      ) : data.car.isPrivateCar ? (
                        <Check icon={SvgCheck} color="green" obscured />
                      ) : (
                        <Check icon={SvgCancel} color="red" obscured />
                      ),
                    },
                  ],
                  [
                    {
                      title: i18n.t.common.category,
                      data: categories.find((x) => x.value == data?.car.category)?.label ?? empty,
                    },
                    {
                      title: i18n.t.common.co2,
                      data: data?.car.co2Emissions ? data?.car.co2Emissions : empty,
                    },
                  ],
                  [
                    {
                      title: data?.carPlate
                        ? i18n.t.plateNumbers.singular
                        : getTitleCarPlate(
                            data?.carPlate?.startDate!,
                            data?.carPlate?.endDate!,
                            !!data?.car.carSubsidiary?.subsidiary.allowMultiPlates,
                          ),
                      data: data?.carPlate?.nr || "---",
                      addElement: !!data?.car.carSubsidiary?.subsidiary.allowMultiPlates &&
                        hasSubsidiaryPermissions &&
                        data?.carPlate && <Box.Icon glyph={SvgEdit} onClick={() => setShowCarPlate(true)} />,
                    },
                    null,
                  ],

                  [
                    {
                      title: i18n.t.common.notes,
                      data: data?.car?.notes ?? empty,
                      colspan: 2,
                    },
                    null,
                  ],
                ]}
              />,
              data?.leasing ? (
                <Box
                  title={i18n.t.common.leasing}
                  onEdit={data && hasSubsidiaryPermissions ? () => setShowLeasing(true) : undefined}
                  fields={[
                    [
                      {
                        title: i18n.t.leasingCompanies.singular,
                        data: data.leasing.leasingCompany.name,
                      },
                      {
                        title: i18n.t.common.contractDate,
                        data: formatDateNumeric(data.leasing.contractDate!, i18n.language),
                      },
                    ],
                    [
                      {
                        title: i18n.t.common.expirationDate,
                        data: formatDateNumeric(data.leasing.expirationDate!, i18n.language),
                      },
                      {
                        title: i18n.t.common.contractDetails,
                        data: data.leasing.contractDetails ? data.leasing.contractDetails : empty,
                      },
                    ],
                  ]}
                />
              ) : (
                <Box
                  title={i18n.t.common.leasing}
                  button={
                    !!data && !!userSettings.permissions.carLeasings.insert && hasSubsidiaryPermissions
                      ? {
                          onClick: () => setShowNewLeasing(true),
                        }
                      : undefined
                  }
                  fields={[
                    [
                      {
                        data: i18n.t.common.noLeasing,
                      },
                      null,
                    ],
                  ]}
                />
              ),
            ]}
          />
        </div>
        <div>
          {data?.rentalCar && (
            <Box.Group
              color="yellow"
              boxs={[
                <Box
                  title={i18n.t.common.carReplacedRental}
                  onEdit={
                    userSettings.permissions.rentalCars.update ? () => setShowEditRental(data.rentalCar) : undefined
                  }
                  fields={[
                    [
                      {
                        title: i18n.t.plateNumbers.singular,
                        data: data?.rentalCar?.plateNumber,
                      },
                      { title: i18n.t.drivers.singular, data: data?.rentalCar?.driver.fullName },
                    ],
                    [
                      {
                        title: i18n.t.common.startDate,
                        data: data?.rentalCar?.startDate
                          ? formatDateNumeric(data?.rentalCar?.startDate!, i18n.language)
                          : "",
                      },
                      {
                        title: i18n.t.common.endDate,
                        data: data?.rentalCar?.endDate
                          ? formatDateNumeric(data?.rentalCar?.endDate!, i18n.language)
                          : empty,
                      },
                    ],
                    [
                      {
                        title: i18n.t.leasingCompanies.singular,
                        data: data.rentalCar.leasingCompany.name,
                      },
                      {
                        title: i18n.t.manufacturers.singular,
                        data: `${data.rentalCar.manufacturer?.name ?? ""}${
                          data.rentalCar.manufacturer?.name && data.rentalCar.model ? " - " : ""
                        }${data.rentalCar.model ?? ""}`,
                      },
                    ],
                  ]}
                />,
              ]}
            ></Box.Group>
          )}
          {userSettings.permissions.highwayPasses.read && (
            <Box.Group
              boxs={[
                data?.highwayPass ? (
                  <Box
                    title={getTitleHighwayPass(data.highwayPass.startDate, data.highwayPass.endDate!)}
                    onEdit={
                      data && hasSubsidiaryPermissions && !!userSettings.permissions.highwayPasses.update
                        ? () => setShowHp(true)
                        : undefined
                    }
                    button={
                      !!data &&
                      hasSubsidiaryPermissions &&
                      (userSettings.permissions.highwayPasses.insert || !!userSettings.permissions.highwayPasses.update)
                        ? { onClick: () => setShowAssignNewHp(true) }
                        : undefined
                    }
                    fields={[
                      [
                        {
                          title: i18n.t.common.number,
                          data: data?.highwayPass?.nr,
                        },
                        null,
                      ],
                      [
                        {
                          title: i18n.t.common.startDate,
                          data: formatDateNumeric(data?.highwayPass?.startDate!, i18n.language),
                        },
                        {
                          title: i18n.t.common.endDate,
                          data: data?.highwayPass?.endDate
                            ? formatDateNumeric(data?.highwayPass?.endDate!, i18n.language)
                            : "--",
                        },
                      ],
                    ]}
                  />
                ) : (
                  <Box
                    title={i18n.t.common.currentHighwayPass}
                    button={
                      !!data && !!userSettings.permissions.highwayPasses.insert && hasSubsidiaryPermissions
                        ? {
                            onClick: () => setShowNewHp(true),
                          }
                        : undefined
                    }
                    fields={[
                      [
                        {
                          data: i18n.t.common.noHighwayPasses,
                        },
                        null,
                      ],
                    ]}
                  />
                ),
              ]}
            />
          )}
          {userSettings.permissions.mileages.read && (
            <Box.Group
              boxs={
                data?.lastMileage ? (
                  <Box
                    title={i18n.t.common.lastMileage}
                    button={
                      !!data && hasSubsidiaryPermissions && !!userSettings.permissions.mileages.insert
                        ? {
                            onClick: () => setShowNewMileage(true),
                          }
                        : undefined
                    }
                    onEdit={
                      data?.lastMileage && hasSubsidiaryPermissions && !!userSettings.permissions.mileages.update
                        ? () => setShowMileages(true)
                        : undefined
                    }
                    listActions={mileageListActions()}
                    fields={[
                      [
                        {
                          title: i18n.t.common.date,
                          data: !!data?.lastMileage
                            ? formatDateNumeric(data?.lastMileage?.date!, i18n.language)
                            : empty,
                        },
                        {
                          title: i18n.t.common.mileage,
                          data: !!userSettings.me.distanceInMiles
                            ? `${formatNumber(convertKilometersToMiles(data.lastMileage.distance), i18n.language, {
                                showThousendSeparator: true,
                                fractionalDigits: 0,
                              })} ${i18n.t.common.miles}`
                            : `${formatNumber(data.lastMileage.distance, i18n.language, {
                                showThousendSeparator: true,
                                fractionalDigits: 0,
                              })} km`,
                        },
                      ],
                    ]}
                  />
                ) : (
                  <Box
                    title={i18n.t.common.lastMileage}
                    button={
                      !!data && hasSubsidiaryPermissions && !!userSettings.permissions.mileages.insert
                        ? {
                            onClick: () => setShowNewMileage(true),
                          }
                        : undefined
                    }
                    onEdit={data?.lastMileage && hasSubsidiaryPermissions ? () => setShowMileages(true) : undefined}
                    listActions={mileageListActions()}
                    fields={[
                      [
                        {
                          data: i18n.t.common.noMilegaes,
                        },
                        null,
                      ],
                    ]}
                  />
                )
              }
            />
          )}
          <Box.Group
            boxs={
              !data?.currentAssignment.carDriver && !data?.currentAssignment.otherCarAssignment ? (
                <>
                  <Box
                    title={i18n.t.common.currentDriver}
                    listActions={assignmentListActions()}
                    fields={[
                      [
                        {
                          data: i18n.t.common.noDriver,
                        },
                        null,
                      ],
                    ]}
                  />
                </>
              ) : !!data.currentAssignment.carDriver ? (
                <Box
                  listActions={assignmentListActions()}
                  title={getTitleDriver(
                    data.currentAssignment.carDriver.startDate,
                    data.currentAssignment.carDriver.endDate!,
                  )}
                  onEdit={
                    data && hasSubsidiaryPermissions && !!userSettings.permissions.carDrivers.update
                      ? () => setShowEditCarDriver(true)
                      : undefined
                  }
                  fields={[
                    [
                      {
                        title: i18n.t.common.name,
                        data: data.currentAssignment.carDriver?.driver.fullName,
                      },
                      {
                        title: i18n.t.common.email,
                        data: data.currentAssignment.carDriver?.driver.email ? (
                          <CustomLink to={`mailto:${data.currentAssignment.carDriver?.driver.email}`}>
                            {data.currentAssignment.carDriver?.driver.email}
                          </CustomLink>
                        ) : undefined,
                      },
                    ],
                    [
                      {
                        title: i18n.t.common.costCenter,
                        data: data.currentAssignment.carDriver.driver.currentCostCenter ?? empty,
                      },
                      {
                        title: i18n.t.common.birthdate,
                        data: data.car.currentAssignment.carDriver?.driver.birthDate
                          ? formatDateNumeric(data.currentAssignment.carDriver?.driver.birthDate, i18n.language)
                          : empty,
                      },
                    ],
                    [
                      {
                        title: i18n.t.common.startDate,
                        data: formatDateNumeric(data.currentAssignment.carDriver?.startDate, i18n.language),
                      },
                      {
                        title: i18n.t.common.endDate,
                        data: data.currentAssignment.carDriver?.endDate
                          ? formatDateNumeric(data.currentAssignment.carDriver?.endDate, i18n.language)
                          : "---",
                      },
                    ],
                    [
                      {
                        title: i18n.t.common.typeOfUse,
                        data:
                          typesOfUse.find((x) => x.value == data.currentAssignment.carDriver?.typeOfUse)?.label ??
                          empty,
                      },
                      null,
                    ],
                  ]}
                />
              ) : (
                data.currentAssignment.otherCarAssignment && (
                  <Box
                    title={
                      data.currentAssignment.otherCarAssignment.type == "car-pool"
                        ? i18n.t.common.carInCarPool
                        : i18n.t.common.carNotInUse
                    }
                    onEdit={data && hasSubsidiaryPermissions ? () => setShowEditOtherCarAssignment(true) : undefined}
                    listActions={assignmentListActions()}
                    fields={[
                      [
                        {
                          title: i18n.t.common.costCenter,
                          data: data.currentAssignment.otherCarAssignment.costCenter,
                        },
                        null,
                      ],
                      [
                        {
                          title: i18n.t.common.startDate,
                          data: formatDateNumeric(data.currentAssignment.otherCarAssignment.startDate, i18n.language),
                        },
                        {
                          title: i18n.t.common.endDate,
                          data: data.currentAssignment.otherCarAssignment.endDate
                            ? formatDateNumeric(data.currentAssignment.otherCarAssignment.endDate, i18n.language)
                            : "---",
                        },
                      ],
                    ]}
                  />
                )
              )
            }
          />
          {!isInitialized && <LoadingSpinner />}
        </div>
        {(userSettings.permissions.carTasks.read || userSettings.permissions.dueDates.read) && (
          <div>
            {userSettings.permissions.carTasks.read && (
              <Box.Group
                boxs={
                  <Box
                    title={i18n.t.common.tasks}
                    listActions={carTaskListActions()}
                    fields={
                      !tasks?.total
                        ? [
                            [
                              {
                                data: i18n.t.common.noTasksToDo,
                              },
                              null,
                            ],
                          ]
                        : undefined
                    }
                  >
                    {tasks?.total ? (
                      <div>
                        <Table<CarTaskSearch>
                          head={
                            <>
                              <th align="left">{i18n.t.common.name}</th>
                              <th align="left">{i18n.t.common.state}</th>
                              <th align="left">{i18n.t.drivers.singular}</th>
                              <th align="left">WF</th>
                              <th></th>
                            </>
                          }
                          renderRow={(item) => (
                            <>
                              <td>{item.taskName}</td>
                              <td>
                                <State
                                  text={
                                    item.state
                                      ? getTaskStateText(i18n.t, item.state)
                                      : getTaskStateText(i18n.t, undefined)
                                  }
                                  color={getTaskStateColor(item.state)}
                                />
                              </td>
                              <td>{item.driver?.fullName}</td>
                              <td>{getIcon(item)}</td>
                            </>
                          )}
                          rowActions={getTasksRowActions}
                          items={tasks?.items}
                        />

                        {!!itemToShowHistory && (
                          <CarTasksHistory
                            carTaskId={itemToShowHistory.id}
                            taskName={itemToShowHistory.taskName}
                            onClose={() => setItemToShowHistory(undefined)}
                          />
                        )}
                      </div>
                    ) : undefined}
                  </Box>
                }
              />
            )}

            {userSettings.permissions.dueDates.read && (
              <Box.Group
                boxs={
                  data?.dueDates ? (
                    <Box
                      title={i18n.t.dueDates.plural}
                      button={
                        !!data && hasSubsidiaryPermissions && !!userSettings.permissions.dueDates.insert
                          ? {
                              onClick: () => setShowNewDueDate(true),
                            }
                          : undefined
                      }
                    >
                      <div>
                        <Table<DueDate>
                          onRowClick={data && hasSubsidiaryPermissions ? (item) => setDueDateShow(item) : undefined}
                          head={
                            <>
                              <th align="left">{i18n.t.common.type}</th>
                              <th align="left">{i18n.t.common.date}</th>
                              {!!hasSubsidiaryPermissions &&
                                (userSettings.permissions.dueDates.update ||
                                  userSettings.permissions.dueDates.delete) && <th></th>}
                            </>
                          }
                          renderRow={(item) => (
                            <>
                              <td>{item.type.name}</td>
                              <td>{formatDateNumeric(item.date, i18n.language)}</td>
                            </>
                          )}
                          rowActions={getDueDateRowActions}
                          getRowStyle={(item) => {
                            if (isItemExpired(item.date, item.isSettled)) {
                              return { "--row-bg": "var(--red-20)", "--row-hover-bg": "var(--red-40)" } as React.CSSProperties;
                            }
                            if (isItemExpiringSoon(item.date, item.isSettled)) {
                              return { "--row-bg": "var(--yellow-20)", "--row-hover-bg": "var(--yellow-40)" } as React.CSSProperties;
                            }
                            return undefined;
                          }}
                          items={data.dueDates}
                        />
                      </div>
                    </Box>
                  ) : (
                    <Box
                      title={i18n.t.dueDates.plural}
                      button={
                        !!data && hasSubsidiaryPermissions && !!userSettings.permissions.dueDates.insert
                          ? {
                              onClick: () => setShowNewDueDate(true),
                            }
                          : undefined
                      }
                      fields={[
                        [
                          {
                            data: i18n.t.common.noDueDate,
                          },
                          null,
                        ],
                      ]}
                    />
                  )
                }
              />
            )}
          </div>
        )}
      </OverviewGrid>

      {!!showEditCar && !!userSettings.permissions.cars.update && (
        <EditCarFormDialog
          car={data?.car}
          onClose={() => setShowEditCar(false)}
          onSubmit={() => {
            trigger();
            setShowCarPlatesTab(!!data?.car.carSubsidiary?.subsidiary.allowMultiPlates);
            dispatch(showSuccessNotification(i18n.t));
            setShowEditCar(false);
          }}
          isLoading={!data}
          isDisabled={!!overViewError}
        />
      )}

      {!!data?.currentAssignment.carDriver && !!showEditCarDriver && !!userSettings.permissions.carDrivers.update && (
        <EditCarDriverDialog
          carDriverId={data?.currentAssignment.carDriver.id}
          onClose={(success) => {
            setShowEditCarDriver(false);
            success && trigger();
          }}
          carId={carId}
        />
      )}

      {!!showAssignCarDriver && (
        <ModalDialog title={i18n.t.carDrivers.new} onClose={() => setShowAssignCarDriver(false)}>
          <CarDriverForm onSubmit={assignNewDriver} carId={carId} />
        </ModalDialog>
      )}

      {!!showNewCarDriver &&
        !!userSettings.permissions.carDrivers.insert &&
        !!userSettings.permissions.carDrivers.update && (
          <ModalDialog title={i18n.t.common.assignNewDriver} onClose={() => setShowNewCarDriver(false)}>
            <CarDriverForm onSubmit={assignNewDriver} carId={carId} />
          </ModalDialog>
        )}

      {!!data?.highwayPass && !!showHp && !!userSettings.permissions.highwayPasses.update && (
        <EditHighwayPassDialog
          highwayPassId={data?.highwayPass.id}
          onClose={(success) => {
            setShowHp(false);
            success && trigger();
          }}
          carId={carId}
        />
      )}

      {!!showAssignNewHp &&
        !!userSettings.permissions.highwayPasses.update &&
        !!userSettings.permissions.highwayPasses.insert && (
          <ModalDialog title={i18n.t.common.assignHp} onClose={() => setShowAssignNewHp(false)}>
            <HighwayPassForm onSubmit={assignNewHighwayPass} highwayPass={data?.highwayPass} isCarOverview />
          </ModalDialog>
        )}

      {!!showNewHp && !!userSettings.permissions.highwayPasses.insert && (
        <CreateHighwayPassDialog
          onClose={(success) => {
            setShowNewHp(false);
            success && trigger();
          }}
          carId={carId}
        />
      )}

      {!!data?.car.leasing && !!showLeasing && !!userSettings.permissions.carLeasings.update && (
        <EditCarLeasingDialog
          carId={data.car.id}
          onClose={(success) => {
            setShowLeasing(false);
            success && trigger();
          }}
        />
      )}

      {!!showNewLeasing && !!userSettings.permissions.carLeasings.insert && (
        <CreateCarLeasing
          onClose={(success) => {
            setShowNewLeasing(false);
            success && trigger();
          }}
          carId={carId}
        />
      )}

      {!!data?.lastMileage && !!showMileages && !!userSettings.permissions.mileages.update && (
        <EditMileageDialog
          onClose={(success) => {
            setShowMileages(false);
            success && trigger();
          }}
          mileageId={data.lastMileage.id}
          carId={carId}
          allowEdit
        />
      )}

      {!!showNewMileage && !!userSettings.permissions.mileages.insert && (
        <CreateMileageDialog
          onClose={(success) => {
            setShowNewMileage(false);
            success && trigger();
          }}
          carId={carId}
        />
      )}

      {!!data?.currentAssignment.carDriver &&
        !!showSendMileageReminder &&
        !!userSettings.permissions.mileages.insert && (
          <SendMileageReminderDialog
            carId={data.car.id}
            driver={data.currentAssignment.carDriver.driver}
            onClose={(success) => {
              setShowSendMileageReminder(false);
              success && trigger();
            }}
          />
        )}

      {!!showNewDueDate && !!userSettings.permissions.dueDates.insert && (
        <CreateDueDateDialog
          onClose={(success) => {
            setShowNewDueDate(false);
            success && trigger();
          }}
          carId={carId}
        />
      )}

      {!!itemToCreateDueDate && data && !!userSettings.permissions.dueDates.insert && (
        <ModalDialog title={i18n.t.dueDates.new} onClose={() => setItemToCreateDueDate(undefined)}>
          <DueDateFormNuovo
            onSubmit={onSubmitNewDueDate}
            defaultTask={itemToCreateDueDate.defaultTask}
            carId={data?.car.id}
          />
        </ModalDialog>
      )}

      {!!dueDateToEdit && !!userSettings.permissions.dueDates.update && (
        <EditDueDate
          onClose={(success) => {
            setDueDateShow(undefined);
            success && trigger();
          }}
          dueDateId={dueDateToEdit.id}
          carId={carId}
        />
      )}

      {!!data?.carPlate && !!showCarPlate && !!userSettings.permissions.carPlates.update && (
        <EditCarPlateDialog
          onClose={(success) => {
            setShowCarPlate(false);
            success && trigger();
          }}
          carPlateId={data?.carPlate.id}
          carId={carId}
        />
      )}

      {!!showAssignCarPlate && !!userSettings.permissions.carPlates.update && (
        <ModalDialog title={i18n.t.common.assignNewPlate} onClose={() => setShowAssignCarPlate(false)}>
          <CarPlateForm
            onSubmit={assignNewPlate}
            isCarOverview
            carPlate={data?.carPlate}
            isDisabled={!userSettings.permissions.carPlates.update}
          />
        </ModalDialog>
      )}

      {!!showNewCarTask.isOpen && !!userSettings.permissions.carTasks.insert && (
        <CreateCarTaskDialog
          onClose={(success) => {
            setShowNewCarTask({ isOpen: false, isWithDefault: false });
            success && trigger();
          }}
          carId={carId}
          isWithDefaultTask={showNewCarTask.isWithDefault}
        />
      )}

      {!!carTaskToEdit && !!userSettings.permissions.carTasks.update && (
        <EditCarTaskDialog
          onClose={(success) => {
            setCarTaskToEdit(undefined);
            success && trigger();
          }}
          carTaskId={carTaskToEdit.id}
          carId={carId}
        />
      )}

      {!!carId && !!carTaskToDelete && !!userSettings.permissions.carTasks.delete && (
        <DeleteCarTaskDialog
          carTaskId={carTaskToDelete.id}
          carId={carId}
          onClose={(success) => {
            setCarTaskToDelete(undefined);
            success && trigger();
          }}
          workflowId={carTaskToDelete.workflowId}
        />
      )}

      {!!showAgreementDialog && (
        <ModalDialog onClose={() => setShowAgreementDialog(false)} title={i18n.t.common.generateAgreement}>
          <AgreementForm
            onSubmit={onSubmitAgreement}
            car={data?.car}
            isDisabled={!data?.car.carSubsidiary}
            onUpdate={async () => {
              await trigger();
            }}
          />
        </ModalDialog>
      )}

      {!!showCarDriverDelegationDialog && (
        <ModalDialog onClose={() => setShowCarDriverDelegationDialog(false)} title={i18n.t.common.createDelegation}>
          <DelegationForm
            onSubmit={onSubmitCarDriverDelegation}
            carDriverId={data?.currentAssignment?.carDriver?.id}
            onUpdate={async () => {
              await trigger();
            }}
          />
        </ModalDialog>
      )}

      {!!showCarDelegationDialog && (
        <ModalDialog onClose={() => setShowCarDelegationDialog(false)} title={i18n.t.common.createDelegation}>
          <DelegationForm
            onSubmit={onSubmitCarDelegation}
            car={data?.car}
            onUpdate={async () => {
              await trigger();
            }}
          />
        </ModalDialog>
      )}

      {!!showCarDelegationExternalDialog && (
        <ModalDialog
          onClose={() => setShowCarDelegationExternalDialog(false)}
          title={i18n.t.common.createDelegationForExternal}
        >
          <DelegationForm
            onSubmit={(formData) => onSubmitCarDelegation(formData, true)}
            car={data?.car}
            external
            onUpdate={async () => {
              await trigger();
            }}
          />
        </ModalDialog>
      )}

      {!!showAddToOtherCarAssignment.value == true && !!userSettings.permissions.otherCarAssignment.insert && (
        <ModalDialog
          title={
            showAddToOtherCarAssignment.type == "car-pool" ? i18n.t.common.addToCarPool : i18n.t.common.setNotInUse
          }
          onClose={() => setShowAddToOtherCarAssignment({ value: false })}
        >
          <OtherCarAssignmentForm
            onSubmit={addToOtherCarAssignment}
            carId={carId}
            type={showAddToOtherCarAssignment.type}
          />
        </ModalDialog>
      )}

      {!!data?.currentAssignment.otherCarAssignment && !!showEditOtherCarAssignment && (
        <EditOtherCarAssignmentDialog
          otherCarAssignmentId={data?.currentAssignment.otherCarAssignment.id}
          onClose={(success) => {
            setShowEditOtherCarAssignment(false);
            success && trigger();
          }}
          carId={carId}
        />
      )}

      {!!showDueDateTypes && <DueDateTypes onClose={() => setShowDueDateTypes(false)} />}

      {!!showCarSubsidiariesDialog && carId && (
        <CarSubsidiariesDialog
          carId={carId}
          onClose={() => setShowCarSubsidiariesDialog(false)}
          onUpdate={async () => {
            await trigger();
          }}
        />
      )}

      {!!showAssignSubsidiary && carId && (
        <CreateCarSubsidiaryButton
          carId={carId}
          onClose={() => setShowAssignSubsidiary(false)}
          onUpdate={async () => {
            await trigger();
          }}
        />
      )}

      {!!dueDateToDelete && (
        <DeleteDueDateDialog
          dueDateId={dueDateToDelete.id}
          carId={dueDateToDelete.car.id}
          onClose={(success) => {
            setDueDateToDelete(undefined);
            success && trigger();
          }}
        />
      )}

      {!!showNotifyDialog && (
        <ModalDialog
          isSubmitting={isSubmittingNotify}
          onClose={() => {
            setShowNotifyDialog(undefined);
          }}
          title={i18n.t.common.notifyDriver}
          actions={[
            {
              title: i18n.t.common.send,
              disabled: isSubmittingNotify,
              onClick: () => notifyDriver(showNotifyDialog),
            },
          ]}
        >
          <div>{i18n.t.common.emailSendToDriver}</div>
        </ModalDialog>
      )}

      {!!showCheckWorkflow?.workflowId && (
        <CheckWorkflowDialog
          carId={carId!}
          onClose={() => closeWorkflowDialog()}
          workflowId={showCheckWorkflow.workflowId}
        />
      )}

      {!!showNewRental && (
        <CreateRentalCarDialog
          onClose={(success) => {
            setShowNewRental(false);
            success && trigger();
          }}
          carId={carId}
          driver={data?.currentAssignment.carDriver?.driver}
        />
      )}

      {!!showEditRental && (
        <EditRentalCarDialog
          onClose={(success) => {
            setShowEditRental(undefined);
            success && trigger();
          }}
          rentalCarId={showEditRental.id}
          carId={carId}
          driver={data?.currentAssignment.carDriver?.driver}
        />
      )}
    </>
  );
});

export default Overview;
